/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext } from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { ProductList } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { getOrderTemplates, GetOrderTemplatesInput, getPaginatedOrderTemplateLinesAction, GetPaginatedOrderTemplateLinesInput, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { OrderTemplate, IFullProduct, IOrderTemplateLinesState } from '../../../themes/adventureworks/views/common/order-templates';
import {getProductsAlternateItemsAction,GetAlternateItemsCollectionInput} from '../../../actions/get-alternate-items-collection.action';
import {ProductAvailabilitiesForSelectedVariantInput,getProductAvailabilitiesForSelectedVariantAction} from './get-product-availabilities-for-selected-variant';//'/get-product-availabilities-for-selected-variant';
import { IGCGetCustomTradeAgreementResponse } from '../../../actions/AlternateItemsEntity.g';
import { getProductsSalesAgreementAsync } from '../../../actions/getProductsSalesAgreement';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetFullOrderTemplateInput implements IActionInput {
    public orderTemplateId: string;

    public queryResultSettings: IQueryResultSettings;

    constructor(orderTemplateId: string, queryResultSettings: IQueryResultSettings) {
        this.orderTemplateId = orderTemplateId;
        this.queryResultSettings = queryResultSettings;
    }

    public getCacheKey = () => `GetFullOrderTemplate|${this.orderTemplateId}`;

    public getCacheObjectType = () => 'GetFullOrderTemplate';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 * @param orderTemplateId
 */
export const createGetFullOrderTemplateInput = (inputData: ICreateActionContext, orderTemplateId: string): GetFullOrderTemplateInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;

    return new GetFullOrderTemplateInput(orderTemplateId, queryResultSettings);
};

/**
 * The action method for the GetFullOrderTemplate data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function getFullOrderTemplateAction(input: GetFullOrderTemplateInput, ctx: IActionContext): Promise<OrderTemplate> {
    if (!ctx.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order template. User is not authenticated.');
    }

    const inputParams = new GetOrderTemplatesInput(input.orderTemplateId);
    const productLists: ProductList[] = await getOrderTemplates(inputParams, ctx);

    if (!productLists || productLists.length !== 1) {
        throw new Error('Unable to get full order template. Order template was not found.');
    }

    const inputLines = new GetPaginatedOrderTemplateLinesInput(productLists[0].Id, input.queryResultSettings);
    const linesState = await getPaginatedOrderTemplateLinesAction(inputLines, ctx) as IOrderTemplateLinesState;
    let productTradeAgreement: IGCGetCustomTradeAgreementResponse | undefined;
    if(linesState && linesState.lines)
    {
        const prodRecId:number[] =[];
        for(var i=0;i<linesState.lines.length;i++)
        {
            if(linesState.lines[`${i}`].fullProduct)
            {
                prodRecId.push( linesState.lines[`${i}`].fullProduct!.ProductDetails.RecordId);
                const prodId:number = linesState.lines[`${i}`].fullProduct!.ProductDetails.RecordId;
                const avail: ProductAvailabilitiesForSelectedVariantInput = new ProductAvailabilitiesForSelectedVariantInput(prodId,ctx.requestContext.apiSettings.channelId);
                const productavail = await getProductAvailabilitiesForSelectedVariantAction(avail,ctx);
                if(productavail)
                {
                    linesState.lines[`${i}`].productAvailability = productavail;
                }
            }

        }
        productTradeAgreement = await getProductsSalesAgreementAsync({callerContext: ctx},prodRecId);

        const ary2: GetAlternateItemsCollectionInput = new GetAlternateItemsCollectionInput(linesState.lines.map((n) => n.fullProduct?.ProductDetails.ItemId!),ctx);
        const alterProds = await getProductsAlternateItemsAction( ary2, ctx);
        if(alterProds)
        {
            linesState.lines.map(function(data,index){
                const prod:IFullProduct|undefined= data.fullProduct;
                if(prod)
                {
                    prod.AlternateProduct = alterProds.respObj?.filter(x => x.ItemId === data.fullProduct?.ProductDetails.ItemId)[0];
                    data.fullProduct = prod;
                }
            });
        }

        if(productTradeAgreement !== undefined && productTradeAgreement.gcProductTradeAgreementsEntity && productTradeAgreement.gcProductTradeAgreementsEntity.length > 0)
            {
                linesState.lines.map(async function(data,index){
                    const prod:IFullProduct|undefined= data.fullProduct;
                    if(prod)
                    {
                        prod.TradeAgreement = productTradeAgreement?.gcProductTradeAgreementsEntity?.find(x => x !== undefined && x.ItemId === data.fullProduct?.ProductDetails.ItemId);
                        data.fullProduct = prod;
                    }
                });
            }
    }
    return new OrderTemplate(productLists[`${0}`], linesState.lines, linesState.totalCount);
}

/**
 * The GetFullOrderTemplate Data Action
 * Returns order templates.
 */
export const getFullOrderTemplateActionDataAction = createObservableDataAction<OrderTemplate>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-full-order-template',
    action: <IAction<OrderTemplate>>getFullOrderTemplateAction,
    input: createGetFullOrderTemplateInput
});

export default getFullOrderTemplateActionDataAction;
